import * as React from 'react';
import { createPortal } from 'react-dom';

import clsx from 'clsx';

import '@app/scss/components/modal.scss';

import { Icon } from '@image/icon';

export interface ModalProps {
	open: boolean;
	className?: string;
	style?: React.CSSProperties;
	lockBodyScroll?: boolean;
	allowBackgroundScroll?: boolean;
	onClose: (value: boolean) => void;
	content: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = (props: ModalProps) => {
	const lockBodyScroll = props.lockBodyScroll ?? true;
	const [canCloseByOverlay, setCanCloseByOverlay] = React.useState<boolean>(false);
	const fallbackRootStyle: React.CSSProperties = {
		position: 'fixed',
		top: 0,
		left: 0,
		width: '100%',
		height: '100vh',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		zIndex: 99999,
		visibility: 'visible',
		opacity: 1,
	};
	const fallbackContainerStyle: React.CSSProperties = {
		position: 'relative',
		background: '#fff',
		maxHeight: 'calc(100vh - 32px)',
		overflow: 'auto',
		...props.style,
	};

	React.useEffect(() => {
		if (!lockBodyScroll) return undefined;

		document.body.classList.toggle('no-scroll-body', props.open);

		return () => {
			document.body.classList.remove('no-scroll-body');
		};
	}, [lockBodyScroll, props.open]);

	React.useEffect(() => {
		if (!props.open) {
			setCanCloseByOverlay(false);

			return undefined;
		}

		const timeout = window.setTimeout(() => {
			setCanCloseByOverlay(true);
		}, 200);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [props.open]);

	if (!props.open) return null;

	return createPortal(
		<div
			className={clsx('ovc-modal', 'ovc-modal-show', props.allowBackgroundScroll && 'ovc-modal--allow-background-scroll')}
			style={fallbackRootStyle}
		>
			<div
				className="ovc-modal__overlay"
				onClick={() => {
					if (canCloseByOverlay) {
						props.onClose(false);
					}
				}}
			/>
			<div
				className={clsx('ovc-modal__container', props.className)}
				style={fallbackContainerStyle}
			>
				<button
					type="button"
					onClick={() => props.onClose(false)}
					className="ovc-modal__btn-close"
				>
					<Icon.Clear />
				</button>
				{props.content}
			</div>
		</div>,
		document.body,
	);
};
